import React from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import MainLayout from "../Layouts/main";
import TablesSidebar from "./components/TablesSidebar";
import CreateDatabaseOrTableModal from "./CreateDatabaseOrTableModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@shadcn-in-nx/ui/table";
import { Button } from "@shadcn-in-nx/ui/button";
import { Plus, Trash2 } from "lucide-react";
import { Checkbox } from "@shadcn-in-nx/ui/checkbox";
import { toast } from "sonner";
import TableRowComponent from "./components/TableRowComponent";
import { useAuth } from "../context/AuthContext";
import PlanRestrictionAlert from "../Chat/ChatTwoColumnPage/PlanRestrictionAlert";
import { useStagedChanges } from "./hooks/useStagedChanges";
import { useColumnSettings } from "./hooks/useColumnSettings";
import { useExport } from "./hooks/useExport";
import { useCellSelection } from "./hooks/useCellSelection";
import { useKeyboardNav } from "./hooks/useKeyboardNav";
import StagedChangesToolbar from "./components/StagedChangesToolbar";
import ExportButton from "./components/ExportButton";
import HiddenColumnsIndicator from "./components/HiddenColumnsIndicator";
import ColumnHeader from "./components/ColumnHeader";
import ColumnOrganizerDialog from "./components/ColumnOrganizerDialog";
import ComplexJsonDialog from "./components/ComplexJsonDialog";
import TableFilterBar from "./components/TableFilterBar";
import TablePagination from "./components/TablePagination";
import TableColumnDialog from "./components/TableColumnDialog";
import TableSchemaDialog from "./components/TableSchemaDialog";
import TableFilterDialog from "./components/TableFilterDialog";
import { useTableData } from "./context/TableDataContext";

function TableRecords() {
    const { t } = useTranslation();
    const { databaseId, tableId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { hasActiveSubscription, isAdmin, isInTrial } = useAuth();
    const {
        getTableData,
        fetchTableData,
        invalidateTable,
        isStale,
        refreshDatabaseInfo,
        searchQuery,
        setSearchQuery,
        searchDraft,
        setSearchDraft,
        filterRules,
        setFilterRules,
        columnFilters,
        setColumnFilters,
        currentPage,
        setCurrentPage,
    } = useTableData();

    const [records, setRecords] = React.useState(() => {
        const cached = getTableData(
            tableId,
            currentPage,
            searchQuery,
            filterRules,
            columnFilters
        );
        return cached?.records || [];
    });
    const [isAddTableModalOpen, setIsAddTableModalOpen] = React.useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [tableToDelete, setTableToDelete] = React.useState(null);
    const [isDeletingTable, setIsDeletingTable] = React.useState(false);
    // Sidebar collapsed by default when clicking table, open when clicking database
    const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(
        location.state?.sidebarOpen === false
            ? true
            : location.state?.sidebarOpen === true
            ? false
            : true
    );
    const { call: deleteTableCall } = useBluelagoon({ method: "deleteTable" });
    const [editingCell, setEditingCell] = React.useState(null);
    const [newRecord, setNewRecord] = React.useState(null);
    const [initialLoadDone, setInitialLoadDone] = React.useState(() => {
        return !!getTableData(
            tableId,
            currentPage,
            searchQuery,
            filterRules,
            columnFilters
        );
    });
    const itemsPerPage = 25;
    const [selectedRecords, setSelectedRecords] = React.useState(new Set());
    const [response, setResponse] = React.useState(() => {
        const cached = getTableData(
            tableId,
            currentPage,
            searchQuery,
            filterRules,
            columnFilters
        );
        return cached ? { data: cached } : null;
    });
    const [showColumnDialog, setShowColumnDialog] = React.useState(false);
    const [complexJsonDialog, setComplexJsonDialog] = React.useState(null);
    const [editingColumnName, setEditingColumnName] = React.useState(null);
    const [tempColumnName, setTempColumnName] = React.useState("");
    const [pageInput, setPageInput] = React.useState("1");
    const tableContainerRef = React.useRef(null);
    const [showFilterDialog, setShowFilterDialog] = React.useState(false);
    const [filterColumnName, setFilterColumnName] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);

    const { call: saveRecord } = useBluelagoon({
        method: "saveTableRecord",
    });

    const { call: deleteManyRecord } = useBluelagoon({
        method: "deleteManyRecord",
    });

    const { call: addColumn } = useBluelagoon({
        method: "addTableColumn",
    });

    const { call: updateColumn } = useBluelagoon({
        method: "updateTableColumn",
    });

    const { call: deleteColumn } = useBluelagoon({
        method: "deleteTableColumn",
    });

    const [savingRecordId, setSavingRecordId] = React.useState(null);
    const [isApplying, setIsApplying] = React.useState(false);

    // New Beekeeper-style hooks
    const stagedChanges = useStagedChanges();
    const columnSettings = useColumnSettings(tableId);

    // Restore cached data or reset when navigating to a different table
    React.useEffect(() => {
        stagedChanges.reset();
        setSelectedRecords(new Set());
        setEditingCell(null);
        setNewRecord(null);
        setPageInput(String(currentPage));
        const cached = getTableData(
            tableId,
            currentPage,
            searchQuery,
            filterRules,
            columnFilters
        );
        if (cached) {
            setResponse({ data: cached });
            setRecords(cached.records || []);
            setInitialLoadDone(true);
        } else {
            setResponse(null);
            setRecords([]);
            setInitialLoadDone(false);
        }
    }, [tableId]);

    const fetchSeqRef = React.useRef(0);

    const fetchRecords = React.useCallback(
        async (page, search, filters, colFilters) => {
            const seq = ++fetchSeqRef.current;
            setIsLoading(true);
            try {
                const res = await fetchTableData(
                    tableId,
                    page,
                    search,
                    filters,
                    colFilters
                );
                if (seq !== fetchSeqRef.current) return;
                setResponse(res);
                if (res?.data?.records) {
                    setRecords(res.data.records);
                }
            } finally {
                if (seq === fetchSeqRef.current) setIsLoading(false);
            }
        },
        [fetchTableData, tableId]
    );

    const [fetchTrigger, setFetchTrigger] = React.useState(0);

    React.useEffect(() => {
        if (!tableId) return;
        fetchRecords(currentPage, searchQuery, filterRules, columnFilters);
    }, [tableId, currentPage, fetchTrigger]);

    React.useEffect(() => {
        if (response?.data?.records) {
            setInitialLoadDone(true);
            // Update page input to match current page
            if (response?.data?.pagination?.currentPage) {
                setPageInput(String(response.data.pagination.currentPage));
            }
        }
    }, [response]);

    const columns = React.useMemo(() => {
        if (!response?.data?.schema) return [];
        const SYSTEM_COLUMN_NAMES = new Set([
            "status",
            "createdBy",
            "updatedBy",
            "createdAt",
            "updatedAt",
        ]);
        // Filter out id, key, and system columns from schema if they exist; we add them explicitly below
        const schemaColumns = Object.entries(response.data.schema)
            .filter(([name]) => name !== "id" && name !== "key" && !SYSTEM_COLUMN_NAMES.has(name))
            .map(([name, details]) => ({
                name,
                ...details,
            }));

        const systemColumns = [
            { name: "status", type: "string", required: false, description: "Record status", system: true },
            { name: "createdBy", type: "string", required: false, description: "Created by user ID", system: true },
            { name: "updatedBy", type: "string", required: false, description: "Last updated by user ID", system: true },
            { name: "createdAt", type: "datetime", required: false, description: "Created timestamp", system: true },
            { name: "updatedAt", type: "datetime", required: false, description: "Last updated timestamp", system: true },
        ];

        // Add id and key columns at the beginning, system columns at the end
        return [
            {
                name: "id",
                type: "number",
                required: false,
                description: "Record ID",
            },
            {
                name: "key",
                type: "string",
                required: false,
                description: "Record Key",
            },
            ...schemaColumns,
            ...systemColumns,
        ];
    }, [response?.data?.schema]);

    // Filter and order columns based on settings
    const visibleColumns = React.useMemo(() => {
        const visible = columns.filter(
            (col) => !columnSettings.hiddenColumns.has(col.name)
        );

        // If we have a saved order, use it
        if (columnSettings.columnOrder.length > 0) {
            const orderMap = new Map(
                columnSettings.columnOrder.map((name, i) => [name, i])
            );
            return [...visible].sort((a, b) => {
                const aOrder = orderMap.has(a.name)
                    ? orderMap.get(a.name)
                    : 999;
                const bOrder = orderMap.has(b.name)
                    ? orderMap.get(b.name)
                    : 999;
                return aOrder - bOrder;
            });
        }
        return visible;
    }, [columns, columnSettings.hiddenColumns, columnSettings.columnOrder]);

    // Initialize column order when columns load
    React.useEffect(() => {
        if (columns.length > 0 && columnSettings.columnOrder.length === 0) {
            columnSettings.reorderColumns(columns.map((c) => c.name));
        }
    }, [columns]);

    // Sort records based on column settings
    const sortedRecords = React.useMemo(() => {
        if (!columnSettings.sortConfig.column) return records;
        const { column, direction } = columnSettings.sortConfig;
        return [...records].sort((a, b) => {
            const aVal = a[column];
            const bVal = b[column];
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return direction === "asc" ? cmp : -cmp;
        });
    }, [records, columnSettings.sortConfig]);

    // O(1) record lookup map — stabilizes handleCellClick / handleSaveCell deps
    const recordsById = React.useMemo(
        () => new Map(records.map((r) => [r.id, r])),
        [records]
    );

    // Export hook
    const exportHook = useExport(
        response?.data?.tableName || "table",
        columns,
        tableId
    );

    // Cell click handler - needs to be defined before hooks that use it
    const handleCellClick = React.useCallback(
        (recordId, columnName) => {
            const record = recordsById.get(recordId);
            if (!record) return;

            const column = columns.find((c) => c.name === columnName);
            if (column?.system) return; // system columns are read-only

            const value = record[columnName];

            // Check if it's a complex JSON/array
            if (typeof value === "object" && value !== null) {
                setComplexJsonDialog({
                    open: true,
                    recordId,
                    columnName,
                    value: value,
                });
                return;
            }

            setEditingCell({ recordId, columnName });
        },
        [recordsById, columns]
    );

    // Selection and keyboard hooks
    const cellSelection = useCellSelection(sortedRecords, visibleColumns);
    const keyboardNav = useKeyboardNav(
        sortedRecords,
        visibleColumns,
        handleCellClick
    );

    // Apply all staged changes
    const handleApplyChanges = async () => {
        setIsApplying(true);
        try {
            const promises = [];
            // Get IDs of new records (they have negative IDs or isNew flag)
            const newRecordIds = new Set(
                stagedChanges.inserts.map((r) => r.id)
            );

            // Apply inserts - skip records that are also marked for deletion
            stagedChanges.inserts.forEach((record) => {
                if (stagedChanges.deletes.has(record.id)) return; // Skip if deleted
                const data = { ...record };
                delete data.id;
                delete data.isNew;
                delete data.key;
                delete data.createdAt;
                delete data.updatedAt;
                promises.push(saveRecord({ tableId, key: record.key, data }));
            });
            // Apply updates
            stagedChanges.updates.forEach((change, recordId) => {
                const data = { ...change.modified };
                delete data.id;
                delete data.key;
                delete data.createdAt;
                delete data.updatedAt;
                promises.push(
                    saveRecord({
                        tableId,
                        recordId,
                        key: change.modified.key,
                        data,
                    })
                );
            });
            // Apply deletes - only for existing records (not new ones)
            const realDeletes = Array.from(stagedChanges.deletes).filter(
                (id) => !newRecordIds.has(id)
            );
            if (realDeletes.length > 0) {
                promises.push(
                    deleteManyRecord({ tableId, recordIds: realDeletes })
                );
            }
            await Promise.all(promises);
            stagedChanges.reset();
            invalidateTable(tableId);
            fetchRecords(currentPage, searchQuery, filterRules, columnFilters);
            toast.success(t("tableRecords.toasts.applySuccess"));
        } catch (error) {
            toast.error(t("tableRecords.toasts.applyError"));
        } finally {
            setIsApplying(false);
        }
    };

    // Reset all staged changes and revert to server state
    const handleResetChanges = () => {
        stagedChanges.reset();
        invalidateTable(tableId);
        fetchRecords(currentPage, searchQuery, filterRules, columnFilters);
        toast.success(t("tableRecords.toasts.allDiscarded"));
    };

    // Generate SQL for staged changes
    const handleCopySQL = () => {
        const tableName = response?.data?.tableName || "table";
        let sql = `-- SQL for ${tableName}\n`;
        stagedChanges.inserts.forEach((r) => {
            const cols = Object.keys(r).filter(
                (k) => !["id", "isNew"].includes(k)
            );
            const vals = cols.map((c) =>
                r[c] == null ? "NULL" : `'${String(r[c]).replace(/'/g, "''")}'`
            );
            sql += `INSERT INTO ${tableName} (${cols.join(
                ", "
            )}) VALUES (${vals.join(", ")});\n`;
        });
        stagedChanges.updates.forEach((change, id) => {
            const sets = Object.keys(change.modified)
                .filter((k) => change.original[k] !== change.modified[k])
                .map(
                    (k) =>
                        `${k} = '${String(change.modified[k]).replace(
                            /'/g,
                            "''"
                        )}'`
                );
            if (sets.length)
                sql += `UPDATE ${tableName} SET ${sets.join(
                    ", "
                )} WHERE id = ${id};\n`;
        });
        if (stagedChanges.deletes.size > 0) {
            sql += `DELETE FROM ${tableName} WHERE id IN (${[
                ...stagedChanges.deletes,
            ].join(", ")});\n`;
        }
        navigator.clipboard.writeText(sql);
        toast.success(t("tableRecords.toasts.sqlCopied"));
    };

    // Keyboard handler for table
    const handleTableKeyDown = (e) => {
        if (e.key === "c" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            cellSelection.copySelection();
            return;
        }
        if (e.key === "Escape") {
            cellSelection.clearSelection();
            return;
        }
        keyboardNav.handleKeyDown(e, editingCell !== null);
    };

    const handleClearEditingCell = React.useCallback(() => {
        setEditingCell(null);
    }, []);

    const handleOpenModal = React.useCallback((recordId, columnName, value) => {
        setComplexJsonDialog({ open: true, recordId, columnName, value });
    }, []);

    const handleToggleVisibility = React.useCallback(
        (name) => {
            if (columnSettings.hiddenColumns.has(name)) {
                columnSettings.showColumn(name);
            } else {
                columnSettings.hideColumn(name);
            }
        },
        [columnSettings]
    );

    // Stage cell changes instead of auto-saving
    const handleSaveCell = React.useCallback(
        (recordId, newValue, columnName) => {
            const record = recordsById.get(recordId);
            if (!record) return;

            const originalRecord = { ...record };
            const modifiedRecord = {
                ...record,
                [columnName]: newValue,
            };

            // Update local state immediately (optimistic UI)
            setRecords((currentRecords) =>
                currentRecords.map((r) =>
                    r.id === recordId ? modifiedRecord : r
                )
            );

            // Stage the change (don't save to API yet)
            if (record.isNew) {
                // For new records, update the staged insert
                stagedChanges.updateInsert(recordId, {
                    [columnName]: newValue,
                });
            } else {
                // For existing records, stage an update
                stagedChanges.stageUpdate(
                    recordId,
                    originalRecord,
                    modifiedRecord
                );
            }
        },
        [recordsById, stagedChanges]
    );

    // Stage new record instead of auto-saving
    const addNewRecord = () => {
        if (!columns || columns.length === 0) {
            toast.error(t("tableRecords.toasts.noColumnsToAdd"));
            return;
        }

        const tempId = -Date.now();
        const tempKey = `record-${Date.now()}`;
        const emptyRecord = {
            id: tempId,
            key: tempKey,
            ...columns
                .filter((col) => col.name !== "id" && col.name !== "key")
                .reduce((acc, col) => ({ ...acc, [col.name]: "" }), {}),
            isNew: true,
        };

        // Add to local state and stage the insert
        setRecords((currentRecords) => [...currentRecords, emptyRecord]);
        stagedChanges.stageInsert(emptyRecord);
        setNewRecord(tempId);

        // Set editing on first editable column
        const firstEditableCol = columns.find((c) => c.name !== "id");
        if (firstEditableCol) {
            setEditingCell({
                recordId: tempId,
                columnName: firstEditableCol.name,
            });
        }

        toast.success(t("tableRecords.toasts.newRecordAdded"));
    };

    const handleSelectRecord = (recordId) => {
        setSelectedRecords((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(recordId)) {
                newSet.delete(recordId);
            } else {
                newSet.add(recordId);
            }
            return newSet;
        });
    };

    // Stage deletes instead of deleting immediately
    const handleDeleteSelected = () => {
        const recordIdsToDelete = Array.from(selectedRecords);
        const count = recordIdsToDelete.length;

        stagedChanges.stageDelete(recordIdsToDelete);
        setSelectedRecords(new Set());
        toast.success(t("tableRecords.toasts.markedForDeletion", { count }));
    };

    // Delete single row
    const handleDeleteRow = (recordId) => {
        stagedChanges.stageDelete([recordId]);
        toast.success(t("tableRecords.toasts.rowMarkedForDeletion"));
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            // Select all records
            const allRecordIds = records.map((record) => record.id);
            setSelectedRecords(new Set(allRecordIds));
        } else {
            // Deselect all records
            setSelectedRecords(new Set());
        }
    };

    const isComplexValue = (value) => {
        return value !== null && typeof value === "object";
    };

    const addFilter = () => {
        if (columns.length === 0) {
            toast.error(t("tableRecords.toasts.noColumnsAvailable"));
            return;
        }
        setFilterRules([
            ...filterRules,
            {
                id: Math.random().toString(),
                field: columns[0].name,
                operator: "equals",
                value: "",
            },
        ]);
    };

    const removeFilter = (id) => {
        setFilterRules(filterRules.filter((f) => f.id !== id));
    };

    const updateFilter = (id, key, value) => {
        setFilterRules(
            filterRules.map((f) => (f.id === id ? { ...f, [key]: value } : f))
        );
    };

    const triggerFetch = React.useCallback(
        () => setFetchTrigger((n) => n + 1),
        []
    );

    const applyFilters = () => {
        setSearchQuery(searchDraft);
        setCurrentPage(1);
        // Use setTimeout so state updates settle before the fetch trigger
        setTimeout(triggerFetch, 0);
        if (filterRules.length === 0 && !searchDraft) {
            toast.success(t("tableRecords.toasts.filtersCleared"));
            return;
        }
        const hasSearch = !!searchDraft;
        const filterCount = filterRules.length;
        let message;
        if (hasSearch && filterCount > 0) {
            message = t("tableRecords.toasts.appliedSearchAndFilters", {
                count: filterCount,
            });
        } else if (hasSearch) {
            message = t("tableRecords.toasts.appliedSearch");
        } else {
            message = t("tableRecords.toasts.appliedFiltersOnly", {
                count: filterCount,
            });
        }
        toast.success(message);
    };

    // Column management functions
    const handleAddColumn = async (columnName, columnType) => {
        if (!columnName?.trim()) {
            toast.error(t("tableRecords.toasts.enterColumnName"));
            return;
        }

        const type = columnType || "string";

        try {
            await addColumn({
                tableId,
                columnName,
                columnSchema: {
                    type,
                    required: false,
                    description: "",
                },
            });
            setShowColumnDialog(false);
            invalidateTable(tableId);
            fetchRecords(currentPage, searchQuery, filterRules, columnFilters);
            toast.success(t("tableRecords.toasts.columnAdded"));
        } catch (error) {
            let errorMessage = t("tableRecords.toasts.failedToAddColumn");

            if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.response?.error) {
                errorMessage = error.response.error;
            } else if (error?.response?.message) {
                errorMessage = error.response.message;
            } else if (error?.error) {
                errorMessage = error.error;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            if (
                errorMessage.toLowerCase().includes("exists") ||
                errorMessage.toLowerCase().includes("duplicate")
            ) {
                errorMessage = t("tableRecords.toasts.columnExists");
            }

            toast.error(errorMessage);
        }
    };

    const handleUpdateColumnName = async (oldName, newName) => {
        if (!newName.trim() || oldName === newName) {
            setEditingColumnName(null);
            return;
        }

        try {
            await updateColumn({
                tableId,
                oldColumnName: oldName,
                newColumnName: newName,
            });
            setEditingColumnName(null);
            invalidateTable(tableId);
            fetchRecords(currentPage, searchQuery, filterRules, columnFilters);
            toast.success(t("tableRecords.toasts.columnRenamed"));
        } catch (error) {
            let errorMessage = t("tableRecords.toasts.failedToRenameColumn");

            if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.response?.error) {
                errorMessage = error.response.error;
            } else if (error?.response?.message) {
                errorMessage = error.response.message;
            } else if (error?.error) {
                errorMessage = error.error;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            if (
                errorMessage.toLowerCase().includes("exists") ||
                errorMessage.toLowerCase().includes("duplicate")
            ) {
                errorMessage = t("tableRecords.toasts.columnExists");
            }

            toast.error(errorMessage);

            // Keep the column in edit mode for correction
            setTempColumnName(oldName);
        }
    };

    const handleDeleteColumn = async (columnName) => {
        const confirmed = window.confirm(
            t("tableRecords.confirmDeleteColumn", { name: columnName })
        );
        if (!confirmed) {
            return;
        }

        try {
            await deleteColumn({
                tableId,
                columnName,
            });
            invalidateTable(tableId);
            fetchRecords(currentPage, searchQuery, filterRules, columnFilters);
            toast.success(t("tableRecords.toasts.columnDeleted"));
        } catch (error) {
            let errorMessage = t("tableRecords.toasts.failedToDeleteColumn");

            if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.response?.error) {
                errorMessage = error.response.error;
            } else if (error?.response?.message) {
                errorMessage = error.response.message;
            } else if (error?.error) {
                errorMessage = error.error;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            if (
                errorMessage.toLowerCase().includes("required") ||
                errorMessage.toLowerCase().includes("constraint")
            ) {
                errorMessage = t("tableRecords.toasts.cannotDeleteColumn");
            }

            toast.error(errorMessage);
        }
    };

    const handleColumnFilter = (columnName, value) => {
        setColumnFilters((prev) => ({
            ...prev,
            [columnName]: value,
        }));
    };

    const openFilterDialog = (columnName) => {
        setFilterColumnName(columnName);
        setShowFilterDialog(true);
    };

    const handleApplyColumnFilter = React.useCallback(
        (colName, value) => {
            handleColumnFilter(colName, value);
            setShowFilterDialog(false);
            setCurrentPage(1);
            setTimeout(triggerFetch, 0);
            if (value)
                toast.success(
                    t("tableRecords.toasts.filterApplied", { column: colName })
                );
        },
        [triggerFetch, t]
    );

    const handleClearColumnFilter = React.useCallback(
        (colName) => {
            handleColumnFilter(colName, "");
            setShowFilterDialog(false);
            setCurrentPage(1);
            setTimeout(triggerFetch, 0);
            toast.success(
                t("tableRecords.toasts.filterCleared", { column: colName })
            );
        },
        [triggerFetch, t]
    );

    const handleSearchSubmit = React.useCallback(() => {
        setSearchQuery(searchDraft);
        setCurrentPage(1);
        setTimeout(triggerFetch, 0);
    }, [searchDraft, triggerFetch]);

    const handleClearFilters = React.useCallback(() => {
        setSearchDraft("");
        setSearchQuery("");
        setFilterRules([]);
        setCurrentPage(1);
        setTimeout(triggerFetch, 0);
        toast.success(t("tableRecords.toasts.filtersCleared"));
    }, [triggerFetch, t]);

    // Pagination handlers
    const handlePageChange = (newPage) => {
        const totalPages = response?.data?.pagination?.totalPages || 1;
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            // Scroll to top of table when page changes
            if (tableContainerRef.current) {
                tableContainerRef.current.scrollTop = 0;
            }
        }
    };

    const handlePageInputChange = (e) => {
        setPageInput(e.target.value);
    };

    const handlePageInputSubmit = () => {
        const pageNum = parseInt(pageInput);
        if (!isNaN(pageNum) && pageNum >= 1) {
            const totalPages = response?.data?.pagination?.totalPages || 1;
            const validPage = Math.min(pageNum, totalPages);
            setCurrentPage(validPage);
            setPageInput(String(validPage));
            // Scroll to top of table when page changes
            if (tableContainerRef.current) {
                tableContainerRef.current.scrollTop = 0;
            }
        } else {
            // Reset to current page if invalid
            setPageInput(String(currentPage));
        }
    };

    const handlePageInputKeyDown = (e) => {
        if (e.key === "Enter") {
            handlePageInputSubmit();
        }
    };

    // Table sidebar handlers
    const handleAddTableSuccess = () => {
        setIsAddTableModalOpen(false);
        refreshDatabaseInfo();
    };

    const handleDeleteTableFromSidebar = (table) => {
        setTableToDelete(table);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDeleteTable = async (confirmationWord) => {
        if (!tableToDelete) return;
        const deletedTableId = tableToDelete.id;
        setIsDeletingTable(true);
        try {
            await deleteTableCall({
                tableId: deletedTableId,
                confirmationWord,
            });
            setIsDeleteModalOpen(false);
            setTableToDelete(null);
            refreshDatabaseInfo();
            toast.success(t("tableRecords.toasts.tableDeleted"));
            if (deletedTableId === tableId) {
                navigate("/databases");
            }
        } catch (error) {
            let errorMessage = t("tableRecords.toasts.failedToDeleteTable");

            if (error?.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.response?.error) {
                errorMessage = error.response.error;
            } else if (error?.response?.message) {
                errorMessage = error.response.message;
            } else if (error?.error) {
                errorMessage = error.error;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setIsDeletingTable(false);
        }
    };

    const pagination = response?.data?.pagination || {};
    const totalPages = pagination.totalPages || 1;
    const totalRecords = pagination.totalRecords || 0;

    const isActive = hasActiveSubscription || isInTrial;

    return (
        <MainLayout noPadding>
            <div className="flex" style={{ height: "calc(100vh - 65px)" }}>
                {/* Tables Sidebar */}
                <TablesSidebar
                    onAddTable={() => setIsAddTableModalOpen(true)}
                    onDeleteTable={handleDeleteTableFromSidebar}
                    isAdmin={isAdmin}
                    isCollapsed={isSidebarCollapsed}
                    onToggleCollapse={() =>
                        setIsSidebarCollapsed(!isSidebarCollapsed)
                    }
                />

                {/* Main Content */}
                <div className="flex-1 overflow-auto">
                    {!isActive && (
                        <div className="p-4 md:p-6 pb-0">
                            <PlanRestrictionAlert
                                variant="inline"
                                titleKey="databases.upgradeRequired"
                                descriptionKey="databases.freeVersionRestriction"
                            />
                        </div>
                    )}
                    <div
                        className={`flex flex-col h-full bg-background p-2 pb-20 space-y-2 ${
                            !isActive
                                ? "pointer-events-none opacity-50 select-none"
                                : ""
                        }`}
                    >
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            {/* Title and selection */}
                            <div className="flex items-center gap-4">
                                <h1 className="page-title text-lg md:text-xl truncate">
                                    {response?.data?.tableName ||
                                        t("tableRecords.defaultTitle")}
                                </h1>
                                {selectedRecords.size > 0 && (
                                    <div className="flex items-center gap-2 px-2 md:px-3 py-1.5 bg-white border rounded-md">
                                        <span className="text-xs md:text-sm text-muted-foreground">
                                            {t(
                                                "tableRecords.selectedCount",
                                                { count: selectedRecords.size }
                                            )}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleDeleteSelected}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Action buttons - scrollable on mobile */}
                            <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-2 px-2 md:mx-0 md:px-0 md:overflow-visible">
                                <ColumnOrganizerDialog
                                    columns={columns}
                                    hiddenColumns={columnSettings.hiddenColumns}
                                    columnOrder={columnSettings.columnOrder}
                                    onToggleVisibility={handleToggleVisibility}
                                    onReorderColumns={
                                        columnSettings.reorderColumns
                                    }
                                />
                                <HiddenColumnsIndicator
                                    hiddenColumns={columnSettings.hiddenColumns}
                                    onShowColumn={columnSettings.showColumn}
                                    onShowAll={columnSettings.showAllColumns}
                                />
                                <ExportButton
                                    onExportCSV={() =>
                                        exportHook.exportCSV(sortedRecords)
                                    }
                                    onExportJSON={() =>
                                        exportHook.exportJSON(sortedRecords)
                                    }
                                    onExportSQL={() =>
                                        exportHook.exportSQL(sortedRecords)
                                    }
                                />
                                <TableSchemaDialog
                                    columns={columns}
                                    tableName={response?.data?.tableName}
                                />
                                <TableColumnDialog
                                    open={showColumnDialog}
                                    onOpenChange={setShowColumnDialog}
                                    onAddColumn={handleAddColumn}
                                />
                                <Button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        addNewRecord();
                                    }}
                                    disabled={!columns || columns.length === 0}
                                    size="sm"
                                    className="flex-shrink-0"
                                >
                                    <Plus className="w-4 h-4 md:mr-2" />
                                    <span className="hidden md:inline">
                                        {t("tableRecords.addRecord")}
                                    </span>
                                </Button>
                            </div>
                        </div>

                        <TableFilterBar
                            searchDraft={searchDraft}
                            searchQuery={searchQuery}
                            filterRules={filterRules}
                            columns={columns}
                            onSearchDraftChange={setSearchDraft}
                            onSearchSubmit={handleSearchSubmit}
                            onAddFilter={addFilter}
                            onRemoveFilter={removeFilter}
                            onUpdateFilter={updateFilter}
                            onApplyFilters={applyFilters}
                            onClearFilters={handleClearFilters}
                        />

                        {response?.data?.tableDescription && (
                            <p className="text-sm text-muted-foreground">
                                {response.data.tableDescription}
                            </p>
                        )}

                        {/* Staged Changes Toolbar */}
                        <StagedChangesToolbar
                            inserts={stagedChanges.inserts}
                            updates={stagedChanges.updates}
                            deletes={stagedChanges.deletes}
                            onApply={handleApplyChanges}
                            onReset={handleResetChanges}
                            onCopySQL={handleCopySQL}
                            isApplying={isApplying}
                        />

                        <div
                            className="flex-1 rounded-lg border border-border shadow-soft overflow-hidden"
                            tabIndex={0}
                            onKeyDown={handleTableKeyDown}
                        >
                            <div
                                ref={tableContainerRef}
                                className="h-full overflow-auto [&>div]:!overflow-visible [&>div]:!max-h-none focus:outline-none"
                            >
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent border-b border-border bg-background shadow-sm">
                                            <TableHead className="w-[50px] min-w-[50px] bg-background sticky top-0 z-20">
                                                <div
                                                    className={`transition-opacity ${
                                                        selectedRecords.size > 0
                                                            ? "opacity-100"
                                                            : "opacity-0 group-hover:opacity-100"
                                                    }`}
                                                >
                                                    <Checkbox
                                                        checked={
                                                            records.length >
                                                                0 &&
                                                            selectedRecords.size ===
                                                                records.length
                                                        }
                                                        onCheckedChange={
                                                            handleSelectAll
                                                        }
                                                    />
                                                </div>
                                            </TableHead>
                                            {visibleColumns.map((column) => (
                                                <TableHead
                                                    key={column.name}
                                                    className="group relative font-bold text-foreground p-0 border-r border-border bg-background sticky top-0 z-20"
                                                    style={{
                                                        width:
                                                            columnSettings
                                                                .columnWidths[
                                                                column.name
                                                            ] || "auto",
                                                        maxWidth: 300,
                                                    }}
                                                >
                                                    <ColumnHeader
                                                        column={column}
                                                        sortConfig={
                                                            columnSettings.sortConfig
                                                        }
                                                        width={
                                                            columnSettings
                                                                .columnWidths[
                                                                column.name
                                                            ]
                                                        }
                                                        onSort={
                                                            columnSettings.toggleSort
                                                        }
                                                        onHide={
                                                            columnSettings.hideColumn
                                                        }
                                                        onFilter={
                                                            openFilterDialog
                                                        }
                                                        onDelete={
                                                            handleDeleteColumn
                                                        }
                                                    />
                                                </TableHead>
                                            ))}
                                            <TableHead className="w-[100px] min-w-[100px] font-bold text-foreground p-0 bg-background sticky top-0 z-20">
                                                {t("tableRecords.actions")}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {!initialLoadDone && isLoading ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        visibleColumns.length +
                                                        2
                                                    }
                                                >
                                                    <div className="flex justify-center p-4">
                                                        {t("common.loading")}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : sortedRecords.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        visibleColumns.length +
                                                        2
                                                    }
                                                >
                                                    <div className="flex justify-center p-4 text-sm text-muted-foreground">
                                                        {t(
                                                            "tableRecords.noRecordsFound"
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <>
                                                {sortedRecords.map((record) => (
                                                    <TableRowComponent
                                                        key={record.id}
                                                        record={record}
                                                        columns={visibleColumns}
                                                        editingCell={
                                                            editingCell
                                                        }
                                                        newRecord={newRecord}
                                                        selectedRecords={
                                                            selectedRecords
                                                        }
                                                        onSelectRecord={
                                                            handleSelectRecord
                                                        }
                                                        handleCellClick={
                                                            handleCellClick
                                                        }
                                                        handleClearEditingCell={
                                                            handleClearEditingCell
                                                        }
                                                        handleSaveCell={
                                                            handleSaveCell
                                                        }
                                                        savingRecordId={
                                                            savingRecordId
                                                        }
                                                        changeType={stagedChanges.getChangeType(
                                                            record.id,
                                                            record.isNew
                                                        )}
                                                        onOpenModal={
                                                            handleOpenModal
                                                        }
                                                        onDeleteRow={
                                                            handleDeleteRow
                                                        }
                                                    />
                                                ))}
                                                <TableRow
                                                    className="hover:bg-card-hover transition-all duration-200 border-b border-border cursor-pointer"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        addNewRecord();
                                                    }}
                                                >
                                                    <TableCell
                                                        colSpan={
                                                            visibleColumns.length +
                                                            2
                                                        }
                                                        className="p-0"
                                                    >
                                                        <div className="flex justify-center items-center p-2 text-sm text-muted-foreground">
                                                            <Plus className="w-4 h-4 mr-2" />
                                                            {t(
                                                                "tableRecords.addNewRecord"
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>

                        <TablePagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            pageInput={pageInput}
                            isLoading={isLoading}
                            onPageChange={handlePageChange}
                            onPageInputChange={handlePageInputChange}
                            onPageInputSubmit={handlePageInputSubmit}
                            onPageInputKeyDown={handlePageInputKeyDown}
                        />

                        <TableFilterDialog
                            open={showFilterDialog}
                            onOpenChange={setShowFilterDialog}
                            columnName={filterColumnName}
                            currentFilter={columnFilters[filterColumnName]}
                            onApply={handleApplyColumnFilter}
                            onClear={handleClearColumnFilter}
                        />

                        <ComplexJsonDialog
                            dialog={complexJsonDialog}
                            onClose={() => setComplexJsonDialog(null)}
                            onSave={handleSaveCell}
                        />
                    </div>
                </div>

                {/* Add Table Modal */}
                <CreateDatabaseOrTableModal
                    mode="table"
                    databaseId={databaseId}
                    onSuccess={handleAddTableSuccess}
                    open={isAddTableModalOpen}
                    onOpenChange={setIsAddTableModalOpen}
                />

                {/* Delete Table Confirmation Modal */}
                <DeleteConfirmationModal
                    key={tableToDelete?.id || "delete-modal"}
                    open={isDeleteModalOpen}
                    onOpenChange={setIsDeleteModalOpen}
                    onConfirm={handleConfirmDeleteTable}
                    itemType="table"
                    itemName={tableToDelete?.name}
                    isDeleting={isDeletingTable}
                    isAdmin={isAdmin}
                />
            </div>
        </MainLayout>
    );
}

export default TableRecords;
